import type { AdminAiMemory } from '@steado/admin-contract/ai-ops';

import { BadgeCell } from '@/components/table/cells';
import { Field, Fields, Panel } from '@/components/panel/panels';

interface MemoryPanelProps {
  readonly memory: AdminAiMemory;
}

export function MemoryPanel({ memory }: MemoryPanelProps) {
  const { queue } = memory;
  const stalled = memory.awaitingEmbedding > 0;

  return (
    <Panel
      title="Memory extraction"
      action={
        <BadgeCell
          label={queue.isPaused ? 'queue paused' : `${String(queue.counts.failed)} failed`}
          tone={queue.isPaused ? 'warn' : queue.counts.failed > 0 ? 'err' : 'ok'}
          dot
        />
      }
    >
      <div className="flex flex-col gap-3">
        <Fields>
          <Field label={`Written in ${String(memory.windowDays)}d`} value={String(memory.memories.created)} />
          <Field label="Superseded" value={String(memory.memories.superseded)} />
          {/* Not windowed: the coach reads every standing fact, not this month's. */}
          <Field label="Standing facts" value={String(memory.memories.active)} />
          <Field label="Entered by hand" value={String(memory.memories.manual)} />
          <Field label="Queue waiting" value={String(queue.counts.waiting + queue.counts.delayed)} />
          <Field
            label="Awaiting embedding"
            value={
              stalled ? (
                <span className="text-warn-fg">{memory.awaitingEmbedding}</span>
              ) : (
                String(memory.awaitingEmbedding)
              )
            }
          />
        </Fields>

        {stalled ? (
          <p role="note" className="text-[12px] leading-[1.55] text-warn-fg">
            {memory.awaitingEmbedding} standing {memory.awaitingEmbedding === 1 ? 'fact has' : 'facts have'}{' '}
            no embedding yet. Embeddings are written by a background job, so a count that only grows means the
            coach’s similarity search is quietly missing recent facts rather than failing loudly.
          </p>
        ) : null}

        <p className="text-[12px] leading-[1.55] text-muted-foreground">
          Counts only. No memory content reaches this screen. Extracted facts are drawn from a user’s coach
          transcript and belong on the audited user detail screen, not an aggregate.
        </p>
      </div>
    </Panel>
  );
}
